import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormState, UseFormRegister } from 'react-hook-form';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { WatermarkTemplate } from '@frameio/graphql-client/generated';
import { useMeasure } from '@frameio/vapor';
import type { WatermarkBuilderModes } from '../constants';
import {
  LIM_45,
  MAX_BLOCKS,
  MOBILE_MAX_PREVIEW_HEIGHT,
  MOBILE_WIDTH,
  SIDEPANEL_WIDTH,
} from '../constants';
import type {
  AspectRatio,
  WatermarkBlockData,
  WatermarkTemplateFormValues,
} from '../types';
import {
  clampBlockPositionToBounds,
  getBackgroundImageForAspectRatio,
  resolveBlockAnchor,
  transformPosition,
  useRestrictToContainingRect,
} from '../utils/builderUtils';
import { createWatermarkBlock } from './components/blockFactory';
import { ControlsArea } from './components/ControlsArea';
import { PreviewArea } from './components/PreviewArea';

// Block fields whose change alters the rendered footprint, so the anchor may
// need re-clamping back into the frame after they update.
const BOUNDS_AFFECTING_FIELDS: (keyof WatermarkBlockData)[] = [
  'size',
  'rotation',
  'textAlignment',
  'customText',
  'displayName',
  'displayEmail',
  'displayIP',
  'displayDateTime',
  'displayShareCreator',
];

export const animationVariants = {
  variants: {
    enter: {
      x: 100,
      opacity: 0,
    },
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: {
      zIndex: 0,
      x: 100,
      opacity: 0,
    },
  },
  transition: {
    ease: 'easeInOut',
    duration: 0.2,
  },
};

type TemplateData = {
  blocks: WatermarkBlockData[];
  id?: string;
  templateName?: string;
};

type BuilderProps = {
  builderMode: WatermarkBuilderModes;
  scale: number;
  setScale: (scale: number) => void;
  constructBackendFriendlyData?: (
    blocks: WatermarkBlockData[]
  ) => WatermarkBlockData[];
  isSafeZoneVisible?: boolean;
  setTemplateData: (
    templateData: TemplateData | ((prev: TemplateData) => TemplateData)
  ) => void;
  templateData: TemplateData;
  previewDimensions: { width: number; height: number };
  register: UseFormRegister<WatermarkTemplateFormValues>;
  setPreviewDimensions: ({
    width,
    height,
  }: {
    width: number;
    height: number;
  }) => void;
  aspectRatio: AspectRatio;
  previewColumnRef?: React.RefObject<HTMLDivElement | null>;
  templates?: WatermarkTemplate[];
  formState: FormState<WatermarkTemplateFormValues>;
};

export const WatermarkTemplateBuilder: React.FC<BuilderProps> = ({
  builderMode,
  constructBackendFriendlyData,
  isSafeZoneVisible = false,
  setTemplateData,
  templateData,
  previewDimensions,
  setPreviewDimensions,
  aspectRatio,
  register,
  scale,
  setScale,
  previewColumnRef,
  templates,
  formState,
}) => {
  // State declarations
  const isInitialLoad = useRef<boolean>(true);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  // Refs
  const [previewRef, containerDimensions] = useMeasure();

  // Modifier callback for dnd context
  const restrictToContainingRect = useRestrictToContainingRect();

  // DnD sensor setup
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const initialBlock = useMemo(
    () => [
      {
        ...createWatermarkBlock({
          customText: 'Your Custom Text',
          section: 'MIDDLE_CENTER',
        }),
        position: { x: 0.5, y: 0.5 },
      },
    ],
    []
  );

  // Initialize with a default block when opened
  useEffect(() => {
    if (templateData?.blocks?.length === 0 && containerDimensions.width > 0) {
      setTemplateData((prev) => ({
        ...prev,
        blocks: initialBlock,
      }));
      setSelectedBlockId(initialBlock[0].id);
    }
  }, [
    templateData?.blocks?.length,
    containerDimensions.width,
    containerDimensions.height,
    setTemplateData,
    constructBackendFriendlyData,
    initialBlock,
  ]);

  const blockPositionForLoad = useCallback(
    ({
      oldBlocks,
      previewDims,
      scaleOnLoad,
    }: {
      oldBlocks: WatermarkBlockData[];
      previewDims: { width: number; height: number };
      scaleOnLoad: number;
    }) => {
      const newBlocks = oldBlocks.map((block) => {
        const blockElement = document.getElementById(block.id);

        // Return the block unchanged if no DOM element is found
        if (!blockElement) return block;

        const { width: elementWidth, height: elementHeight } =
          blockElement.getBoundingClientRect();

        // Transform position based on the provided function. Uses the block's
        // stored section so a legacy anchor de-normalizes to the correct
        // top-left position.
        const { x, y } = transformPosition(
          block.position.x,
          block.position.y,
          previewDims.width / scaleOnLoad,
          previewDims.height / scaleOnLoad,
          block.section,
          elementWidth / scaleOnLoad,
          elementHeight / scaleOnLoad,
          block.rotation,
          false
        );

        // Normalize legacy anchors on open (e.g. a rotated block saved with a
        // non-center anchor, or a scrolling block saved non-left-aligned). The
        // top-left position was already resolved above using the stored
        // section, so the block stays put visually while its anchor normalizes.
        const { section, textAlignment } = resolveBlockAnchor({
          motion: block.motion,
          rotation: block.rotation,
          section: block.section,
          textAlignment: block.textAlignment,
        });

        return { ...block, position: { x, y }, section, textAlignment };
      });

      return {
        blocks: newBlocks,
      };
    },
    []
  );

  // Adjust block positions on initial load
  useEffect(() => {
    if (!containerDimensions?.width) return;
    if (
      isInitialLoad.current &&
      templateData?.blocks?.length > 0 &&
      previewDimensions.width > 0
    ) {
      const updateBlockPositions = () => {
        setTemplateData((prev) => ({
          ...prev,
          ...blockPositionForLoad({
            oldBlocks: prev.blocks,
            previewDims: previewDimensions,
            scaleOnLoad: scale,
          }),
        }));
        setSelectedBlockId(templateData?.blocks[0]?.id);
      };
      updateBlockPositions();
      isInitialLoad.current = false;
    }
  }, [
    templateData?.blocks,
    containerDimensions,
    previewDimensions,
    scale,
    setTemplateData,
    blockPositionForLoad,
  ]);

  const updateActiveBlockPosition = useCallback(
    ({
      blocks,
      activeBlockId,
      delta,
      sc,
    }: {
      blocks: WatermarkBlockData[];
      activeBlockId: string;
      delta: { x: number; y: number };
      sc: number;
    }) => {
      const updatedBlocks = blocks.map((block) => {
        // Return block unchanged if it's not the active block
        if (block.id !== activeBlockId) return block;

        // Move the block by the scaled drag delta. The block's section (and so
        // its reference point) is intentionally left unchanged: the horizontal
        // component tracks the text alignment, and the vertical component stays
        // stable rather than flipping between TOP/MIDDLE/BOTTOM as the block
        // crosses thirds — which previously made the off-screen drag allowance
        // change unpredictably from one drag to the next.
        const scaledDelta = {
          x: delta.x / sc,
          y: delta.y / sc,
        };

        return {
          ...block,
          position: {
            x: block.position.x + scaledDelta.x,
            y: block.position.y + scaledDelta.y,
          },
        };
      });

      return {
        blocks: updatedBlocks,
      };
    },
    []
  );

  // Handle drag end for blocks
  const handleDragEnd = useCallback(
    ({ active, delta }: DragEndEvent) => {
      if (!active) return;

      setTemplateData((prev) => ({
        ...prev,
        ...updateActiveBlockPosition({
          blocks: prev.blocks,
          activeBlockId: String(active.id),
          delta,
          sc: scale,
        }),
      }));
    },
    [setTemplateData, updateActiveBlockPosition, scale]
  );

  // Add a new block
  const addBlock = useCallback(() => {
    if (templateData?.blocks?.length >= MAX_BLOCKS) return;

    const newBlock = createWatermarkBlock({ section: 'MIDDLE_CENTER' });
    const x = previewDimensions.width / (2 * scale);
    const y = previewDimensions.height / (2 * scale);

    setTemplateData((prev) => {
      const newBlocks = [...prev?.blocks, { ...newBlock, position: { x, y } }];
      return {
        ...prev,
        blocks: newBlocks,
      };
    });
    setSelectedBlockId(newBlock.id);
  }, [
    templateData?.blocks?.length,
    previewDimensions.width,
    previewDimensions.height,
    scale,
    setTemplateData,
  ]);

  const updateBlocksWithSelectedBlock = useCallback(
    ({
      blocks,
      blockId,
      updatedBlock,
      width,
    }: {
      blocks: WatermarkBlockData[];
      blockId: string | null;
      updatedBlock: Partial<WatermarkBlockData>;
      width: number;
    }) => {
      const updatedBlocks = blocks.map((block) => {
        // If the current block doesn't match the selectedBlockId, return it unchanged
        if (block.id !== blockId) return block;

        // Merge the block with the updated properties
        const newBlock = { ...block, ...updatedBlock };

        // for +/- 45 degre rotation, set to very close to 45 to avoid divide by 0 problems for transform position calculations further downstream
        if (newBlock.rotation === 45) {
          newBlock.rotation = LIM_45;
        } else if (newBlock.rotation === -45) {
          newBlock.rotation = -1 * LIM_45;
        }

        // Adjust the position if the motion property has changed,
        // set rotation to 0 if block is in motion since backend does not support rotation and scroll at same time
        if (block.motion !== newBlock.motion) {
          newBlock.position = {
            x: newBlock.motion !== 'none' ? 0 : width / 2,
            y: newBlock.position.y,
          };
          newBlock.rotation =
            newBlock.motion !== 'none' ? 0 : newBlock.rotation;
        }

        // Normalize the block's anchor so the reference point the render
        // service uses stays consistent with what's shown: rotated blocks are
        // center-anchored, scrolling blocks are left-aligned, otherwise the
        // section's horizontal component tracks the chosen alignment. The
        // alignment control is disabled while rotated/scrolling to reflect this.
        const { section, textAlignment } = resolveBlockAnchor({
          motion: newBlock.motion,
          rotation: newBlock.rotation,
          section: newBlock.section,
          textAlignment: newBlock.textAlignment,
        });
        newBlock.section = section;
        newBlock.textAlignment = textAlignment;

        return newBlock;
      });

      return {
        blocks: updatedBlocks,
      };
    },
    []
  );

  // Re-clamp a block's resting position so its section anchor stays within the
  // frame. Reads the block's measured dimensions from the DOM, so it must run
  // after the size change has rendered.
  const reclampBlockToBounds = useCallback(
    (blockId: string) => {
      if (!previewDimensions.width) return;
      const blockEl = document.getElementById(blockId);
      if (!blockEl) return;
      const { width: elementWidth, height: elementHeight } =
        blockEl.getBoundingClientRect();

      setTemplateData((prev) => {
        let changed = false;
        const blocks = prev.blocks.map((block) => {
          if (block.id !== blockId) return block;
          const position = clampBlockPositionToBounds({
            position: block.position,
            section: block.section,
            rotation: block.rotation,
            containerWidth: previewDimensions.width / scale,
            containerHeight: previewDimensions.height / scale,
            elementWidth: elementWidth / scale,
            elementHeight: elementHeight / scale,
          });
          // Skip the update when the block is already in view, so repeated
          // calls (e.g. while dragging the rotation slider) don't churn state.
          if (
            Math.abs(position.x - block.position.x) < 0.5 &&
            Math.abs(position.y - block.position.y) < 0.5
          ) {
            return block;
          }
          changed = true;
          return { ...block, position };
        });
        return changed ? { ...prev, blocks } : prev;
      });
    },
    [previewDimensions, scale, setTemplateData]
  );

  // Update an existing block
  const updateBlock = useCallback(
    (updatedBlock: Partial<WatermarkBlockData>) => {
      setTemplateData((prev) => ({
        ...prev,
        ...updateBlocksWithSelectedBlock({
          blocks: prev.blocks,
          blockId: selectedBlockId,
          updatedBlock,
          width: containerDimensions.width,
        }),
      }));

      // Anything that changes the block's footprint can push its anchor out of
      // the frame: size, rotation (also forces center anchoring), alignment
      // (moves the anchor to a different edge), and the fields that change the
      // rendered text — custom text and the data-point toggles. Re-clamp so the
      // block nudges back into view immediately rather than silently on save.
      const affectsBounds = BOUNDS_AFFECTING_FIELDS.some(
        (field) => updatedBlock[field] !== undefined
      );
      if (affectsBounds && selectedBlockId) {
        requestAnimationFrame(() => reclampBlockToBounds(selectedBlockId));
      }
    },
    [
      setTemplateData,
      updateBlocksWithSelectedBlock,
      selectedBlockId,
      containerDimensions.width,
      reclampBlockToBounds,
    ]
  );

  // Delete a block
  const deleteBlock = useCallback(
    (id: string) => {
      setTemplateData((prev) => {
        if (prev.blocks.length <= 1) return prev;

        const newBlocks = prev.blocks.filter((block) => block.id !== id);

        if (selectedBlockId === id) {
          setSelectedBlockId(newBlocks[0].id);
        }

        return {
          ...prev,
          blocks: newBlocks,
        };
      });
    },
    [selectedBlockId, setTemplateData]
  );

  const backgroundImage = getBackgroundImageForAspectRatio(aspectRatio);

  return (
    <StyledMotionContainer
      animate="center"
      exit="exit"
      initial="enter"
      transition={animationVariants.transition}
      variants={animationVariants.variants}
    >
      <ResponsiveContainer data-testid="watermark-template-builder-content">
        <PreviewColumn ref={previewColumnRef}>
          <DndContext
            modifiers={[restrictToContainingRect]}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <PreviewArea
              ref={previewRef}
              aspectRatio={aspectRatio}
              backgroundImage={backgroundImage}
              blocks={templateData.blocks}
              containerHeight={previewDimensions.height}
              containerWidth={previewDimensions.width}
              onScaleChange={setScale}
              onSelectBlock={setSelectedBlockId}
              safeZone={isSafeZoneVisible}
              selectedBlockId={selectedBlockId || ''}
              setPreviewDimensions={setPreviewDimensions}
            />
          </DndContext>
        </PreviewColumn>
        <ControlColumn>
          <ControlsArea
            addBlock={addBlock}
            blocks={templateData.blocks}
            builderMode={builderMode}
            deleteBlock={deleteBlock}
            formState={formState}
            register={register}
            selectedBlockId={selectedBlockId}
            setSelectedBlockId={setSelectedBlockId}
            setTemplateName={(name) =>
              setTemplateData((prev) => ({ ...prev, templateName: name }))
            }
            templateId={templateData.id || ''}
            templateName={templateData.templateName || ''}
            templates={templates}
            updateBlock={updateBlock}
          />
        </ControlColumn>
      </ResponsiveContainer>
    </StyledMotionContainer>
  );
};

const StyledMotionContainer = styled(motion.div)`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const ResponsiveContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  @media (max-width: ${MOBILE_WIDTH}px) {
    flex-direction: column;
  }
`;

const PreviewColumn = styled.div`
  flex-grow: 1;
  min-width: 0;
  @media (max-width: ${MOBILE_WIDTH}px) {
    width: 100%;
  }
`;

const ControlColumn = styled.div`
  flex-shrink: 0;
  width: ${SIDEPANEL_WIDTH};
  @media (max-width: ${MOBILE_WIDTH}px) {
    width: 100%;
    height: calc(100% - ${MOBILE_MAX_PREVIEW_HEIGHT});
    overflow: scroll;
  }
`;
